import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import VideoPlayer from "../../../components/video/VideoPlayer/VideoPlayer";
import { listAdminMarketplaceProducts } from "../../../features/marketplace/adminMarketplaceApi";
import {
  CATEGORY_LABELS,
  PRODUCT_CATEGORIES,
  SUBTYPE_LABELS,
} from "../../../features/marketplace/marketplaceConstants";
import cls from "./AdminAllProductsPage.module.scss";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "title_asc", label: "Title A-Z" },
  { value: "title_desc", label: "Title Z-A" },
];

const LISTING_STATUS_OPTIONS = ["all", "draft", "active", "paused", "archived"];
const MODERATION_STATUS_OPTIONS = [
  "all",
  "draft",
  "pending",
  "approved",
  "rejected",
];

const getClubLink = (club) => {
  if (!club?._id) return "#";
  if (!club.cityId || !club.streetId) return `/admin/clubs/single/${club._id}`;
  return `/admin/cities/single/${club.cityId}/streets/${club.streetId}/clubs/${club._id}`;
};

function ProductMediaCarousel({ product }) {
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const mediaItems = useMemo(() => {
    const images = (product?.listingImages || []).map((item) => ({
      id: `img-${item.key}`,
      type: "image",
      url: item.url,
    }));

    const videos = (product?.listingVideos || []).map((item) => ({
      id: `vid-${item.videoId}`,
      type: "video",
      videoId: item.videoId,
      url: item.url,
    }));

    return [...images, ...videos];
  }, [product]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [product?._id]);

  if (!mediaItems.length) {
    return (
      <div className={cls.emptyMedia}>
        <span>No media uploaded</span>
      </div>
    );
  }

  const current = mediaItems[index] || mediaItems[0];

  return (
    <div className={cls.mediaWrap}>
      <div className={cls.mediaSquare}>
        {current.type === "image" ? (
          <img
            src={current.url}
            alt={product?.title || "Product media"}
            className={cls.media}
          />
        ) : (
          <>
            {current.videoId ? (
              <VideoPlayer
                className={cls.videoPlayer}
                token={token}
                videoId={current.videoId}
                mode="proxy"
                controls
                muted={false}
              />
            ) : (
              <VideoPlayer
                className={cls.videoPlayer}
                hlsMasterUrl={current.url}
                controls
                muted={false}
              />
            )}
          </>
        )}

        {mediaItems.length > 1 ? (
          <>
            <button
              type="button"
              className={`${cls.navBtn} ${cls.prevBtn}`}
              onClick={() =>
                setIndex(
                  (prev) => (prev - 1 + mediaItems.length) % mediaItems.length,
                )
              }
              aria-label="Previous media"
            >
              ‹
            </button>

            <button
              type="button"
              className={`${cls.navBtn} ${cls.nextBtn}`}
              onClick={() => setIndex((prev) => (prev + 1) % mediaItems.length)}
              aria-label="Next media"
            >
              ›
            </button>
          </>
        ) : null}
      </div>

      {mediaItems.length > 1 ? (
        <div className={cls.dots}>
          {mediaItems.map((item, dotIndex) => (
            <button
              key={item.id}
              type="button"
              className={`${cls.dot} ${dotIndex === index ? cls.dotActive : ""}`}
              onClick={() => setIndex(dotIndex)}
              aria-label={`Go to media ${dotIndex + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminAllProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [subtype, setSubtype] = useState("all");
  const [status, setStatus] = useState("all");
  const [moderationStatus, setModerationStatus] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0, limit: 12 });

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await listAdminMarketplaceProducts({
          page,
          limit: 12,
          sort,
          q: search.trim() || undefined,
          category: category !== "all" ? category : undefined,
          subtype: subtype !== "all" ? subtype : undefined,
          status: status !== "all" ? status : undefined,
          moderationStatus:
            moderationStatus !== "all" ? moderationStatus : undefined,
        });

        if (!mounted) return;

        setProducts(data?.products || []);
        setMeta({
          page: data?.meta?.page || 1,
          pages: data?.meta?.pages || 1,
          total: data?.meta?.total || 0,
          limit: data?.meta?.limit || 12,
        });
      } catch (err) {
        if (!mounted) return;
        setProducts([]);
        setMeta({ page: 1, pages: 1, total: 0, limit: 12 });
        setError(err?.response?.data?.msg || "Could not load products.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [page, sort, search, category, subtype, status, moderationStatus]);

  const subtypeOptions = useMemo(() => {
    const set = new Set();

    products.forEach((item) => {
      if (category !== "all" && item.category !== category) return;
      if (item.serviceType) set.add(item.serviceType);
      if (item.digitalType) set.add(item.digitalType);
    });

    return Array.from(set);
  }, [category, products]);

  const canPrev = page > 1;
  const canNext = page < (meta.pages || 1);
  const start = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const end = Math.min(meta.page * meta.limit, meta.total);

  const jumpToPage = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className={cls.page}>
      <header className={cls.header}>
        <div>
          <p className={cls.kicker}>Marketplace Moderation</p>
          <h1 className={cls.title}>All Products</h1>
          <p className={cls.subtitle}>
            Browse every marketplace product with filters and pagination for
            quick moderation checks.
          </p>
        </div>

        <div className={cls.countBadge}>
          <span className={cls.countNumber}>{meta.total}</span>
          <span className={cls.countText}>total</span>
        </div>
      </header>

      <section className={cls.filtersPanel}>
        <label className={cls.field}>
          <span className={cls.fieldLabel}>Search</span>
          <input
            className={cls.input}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search title, club, or username"
          />
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Category</span>
          <select
            className={cls.input}
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              setSubtype("all");
              setPage(1);
            }}
          >
            <option value="all">All categories</option>
            {Object.values(PRODUCT_CATEGORIES).map((value) => (
              <option key={value} value={value}>
                {CATEGORY_LABELS[value] || value}
              </option>
            ))}
          </select>
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Subtype</span>
          <select
            className={cls.input}
            value={subtype}
            onChange={(event) => {
              setSubtype(event.target.value);
              setPage(1);
            }}
          >
            <option value="all">All subtypes</option>
            {subtypeOptions.map((value) => (
              <option key={value} value={value}>
                {SUBTYPE_LABELS[value] || value}
              </option>
            ))}
          </select>
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Listing Status</span>
          <select
            className={cls.input}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            {LISTING_STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value === "all" ? "All statuses" : value}
              </option>
            ))}
          </select>
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Moderation Status</span>
          <select
            className={cls.input}
            value={moderationStatus}
            onChange={(event) => {
              setModerationStatus(event.target.value);
              setPage(1);
            }}
          >
            {MODERATION_STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value === "all" ? "All moderation" : value}
              </option>
            ))}
          </select>
        </label>

        <label className={cls.field}>
          <span className={cls.fieldLabel}>Sort</span>
          <select
            className={cls.input}
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(1);
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className={cls.paginationBar}>
        <p className={cls.paginationText}>
          Showing {start}–{end} of {meta.total} products
        </p>

        <div className={cls.paginationActions}>
          <button
            type="button"
            className={cls.pageBtn}
            disabled={!canPrev}
            onClick={() => jumpToPage(page - 1)}
          >
            Prev
          </button>
          <span className={cls.pageInfo}>
            Page {meta.page} / {meta.pages}
          </span>
          <button
            type="button"
            className={cls.pageBtn}
            disabled={!canNext}
            onClick={() => jumpToPage(page + 1)}
          >
            Next
          </button>
        </div>
      </section>

      {loading ? (
        <div className={cls.state}>Loading products...</div>
      ) : error ? (
        <div className={cls.error}>{error}</div>
      ) : products.length === 0 ? (
        <div className={cls.state}>No products found for current filters.</div>
      ) : (
        <div className={cls.grid}>
          {products.map((product) => {
            const productSubtype = product.serviceType || product.digitalType;
            const sellerName = product.seller?.username || "Unknown seller";
            const sellerInitial = sellerName.charAt(0).toUpperCase() || "?";
            const clubName = product.club?.clubName || "Unknown club";
            const moderationText = (
              product.moderationStatus || "unknown"
            ).replaceAll("_", " ");
            const statusText = (product.status || "unknown").replaceAll(
              "_",
              " ",
            );

            return (
              <article key={product._id} className={cls.card}>
                <ProductMediaCarousel product={product} />

                <div className={cls.content}>
                  <div className={cls.chips}>
                    <span className={cls.categoryChip}>
                      {CATEGORY_LABELS[product.category] || product.category}
                    </span>
                    {productSubtype ? (
                      <span className={cls.subtypeChip}>
                        {SUBTYPE_LABELS[productSubtype] || productSubtype}
                      </span>
                    ) : null}
                    <span className={cls.subtypeChip}>{moderationText}</span>
                    <span className={cls.subtypeChip}>{statusText}</span>
                  </div>

                  <h2 className={cls.productTitle}>{product.title}</h2>

                  {/* {product.description ? (
                    <p className={cls.description}>{product.description}</p>
                  ) : (
                    <p className={cls.descriptionMuted}>
                      No description provided.
                    </p>
                  )} */}

                  <div className={cls.metaLine}>
                    <span className={cls.metaLabel}>Club</span>
                    {product.club?._id ? (
                      <Link
                        className={cls.metaLink}
                        to={getClubLink(product.club)}
                      >
                        {clubName}
                      </Link>
                    ) : (
                      <span className={cls.metaValue}>{clubName}</span>
                    )}
                  </div>

                  <div className={cls.sellerCard}>
                    <span className={cls.metaLabel}>Seller</span>
                    <div className={cls.memberRow}>
                      <Link
                        className={cls.avatarLink}
                        to={`/admin/members/single/${product.seller?.userId}`}
                      >
                        {product.seller?.avatarUrl ? (
                          <img
                            src={product.seller.avatarUrl}
                            alt={sellerName}
                            className={cls.avatar}
                          />
                        ) : (
                          <div className={cls.avatarFallback}>
                            {sellerInitial}
                          </div>
                        )}
                      </Link>

                      <div className={cls.sellerMeta}>
                        <Link
                          className={cls.metaLink}
                          to={`/admin/members/single/${product.seller?.userId}`}
                        >
                          {sellerName}
                        </Link>
                        <span className={cls.metaValue}>
                          Created:{" "}
                          {new Date(product.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <Link
                      to={`/admin/marketplace/pending-products/${product._id}`}
                      className={cls.viewProductButton}
                    >
                      <span>View Product</span>
                      <span className={cls.viewProductArrow}>→</span>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
